import React, { useState } from "react";
import { FaMinus, FaPlus, FaTimes, FaUserFriends } from "react-icons/fa";

type CustomerDetails = {
  customerName?: string;
  customerEmail?: string;
  customerMobile?: string;
  customerCount?: number;
  waiterName?: string;
};

type Props = {
  open: boolean;
  tableName: string;
  onClose: () => void;
  onSubmit: (details: CustomerDetails) => Promise<void>;
  error?: string;
};

const inputClassName =
  "w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-orange-400/60 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 dark:placeholder:text-slate-500";

const CustomerDetailsForm: React.FC<Props> = ({ open, tableName, onClose, onSubmit, error }) => {
  const [customerName, setCustomerName] = useState("");
  const [customerEmail, setCustomerEmail] = useState("");
  const [customerMobile, setCustomerMobile] = useState("");
  const [customerCount, setCustomerCount] = useState(1);
  const [waiterName, setWaiterName] = useState(() => window.localStorage.getItem("waiterName") || "");
  const [loading, setLoading] = useState(false);

  if (!open) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    await onSubmit({
      customerName: customerName || undefined,
      customerEmail: customerEmail || undefined,
      customerMobile: customerMobile || undefined,
      customerCount,
      waiterName: waiterName || undefined,
    });
    setLoading(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-slate-950/55 p-2 backdrop-blur-sm sm:items-center sm:p-4">
      <div className="w-full max-w-md overflow-hidden rounded-[1.8rem] border border-gray-200/90 bg-white shadow-[0_24px_60px_rgba(15,23,42,0.32)] dark:border-slate-700/80 dark:bg-slate-900">
        <div className="border-b border-gray-200 px-4 pb-4 pt-3 dark:border-slate-800 sm:px-5">
          <div className="mb-3 flex items-center justify-between">
            <div className="h-1.5 w-12 rounded-full bg-slate-300 dark:bg-slate-700 sm:hidden" />
            <button
              onClick={onClose}
              disabled={loading}
              className="ml-auto flex h-9 w-9 items-center justify-center rounded-full bg-slate-900/5 text-slate-500 disabled:opacity-50 dark:bg-white/10 dark:text-slate-300"
              aria-label="Close modal"
            >
              <FaTimes size={14} />
            </button>
          </div>

          <div className="flex items-start gap-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-orange-500 text-white shadow-lg shadow-orange-500/25">
              <FaUserFriends size={18} />
            </div>
            <div>
              <p className="text-[11px] font-bold uppercase tracking-[0.24em] text-orange-600">
                Start Session
              </p>
              <h2 className="mt-1 text-lg font-black text-slate-900 dark:text-slate-100">{tableName}</h2>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4 px-4 py-4 sm:px-5">
          <div className="grid gap-3">
            <input
              type="text"
              value={customerName}
              onChange={(e) => setCustomerName(e.target.value)}
              placeholder="Guest name"
              className={inputClassName}
            />

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <input
                type="tel"
                value={customerMobile}
                onChange={(e) => setCustomerMobile(e.target.value)}
                placeholder="Mobile number"
                className={inputClassName}
              />
              <input
                type="email"
                value={customerEmail}
                onChange={(e) => setCustomerEmail(e.target.value)}
                placeholder="Email address"
                className={inputClassName}
              />
            </div>

            <input
              type="text"
              value={waiterName}
              onChange={(e) => {
                setWaiterName(e.target.value);
                window.localStorage.setItem("waiterName", e.target.value);
              }}
              placeholder="Assigned waiter"
              className={inputClassName}
            />
          </div>

          <div className="rounded-[1.5rem] border border-gray-200 bg-gray-50/90 p-3 dark:border-slate-800 dark:bg-slate-950/80">
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">No. of Guests</p>
              </div>
              <div className="flex items-center gap-2 rounded-full bg-slate-100 p-1 dark:bg-slate-800">
                <button
                  type="button"
                  onClick={() => setCustomerCount((value) => Math.max(1, value - 1))}
                  className="flex h-9 w-9 items-center justify-center rounded-full bg-white text-slate-700 shadow-sm dark:bg-slate-700 dark:text-slate-100"
                >
                  <FaMinus size={11} />
                </button>
                <div className="min-w-[3rem] text-center text-lg font-black text-slate-900 dark:text-slate-100">
                  {customerCount}
                </div>
                <button
                  type="button"
                  onClick={() => setCustomerCount((value) => Math.min(20, value + 1))}
                  className="flex h-9 w-9 items-center justify-center rounded-full bg-orange-500 text-white shadow-lg shadow-orange-500/25"
                >
                  <FaPlus size={11} />
                </button>
              </div>
            </div>
          </div>

          {error && (
            <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-500/20 dark:bg-red-950/40 dark:text-red-300">
              {error}
            </div>
          )}

          <div className="grid grid-cols-2 gap-3 pb-[calc(env(safe-area-inset-bottom))]">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="rounded-2xl border border-slate-200 px-4 py-3 text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-50 disabled:opacity-50 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="rounded-2xl bg-slate-950 px-4 py-3 text-sm font-semibold text-white shadow-[0_14px_30px_rgba(15,23,42,0.2)] transition-transform active:scale-[0.98] disabled:opacity-50 dark:bg-orange-500 dark:text-white"
            >
              {loading ? "Saving..." : "Continue"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CustomerDetailsForm;

